import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { FullstoryService, fullstoryToken } from 'go-modules/services/fullstory/fullstory.service';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { ModifyCollectionsPanelComponent } from '@go-modules/components/library/modify-collections-panel/modify-collections-panel.component';
import { GoSidepanelService } from '@go-modules/services/go-sidepanel/go-sidepanel.service';
import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LibraryService } from '@go-modules/services/library/library.service';
import { BehaviorSubject, filter } from 'rxjs';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_SIDEPANEL_DATA } from '@go-modules/services/go-sidepanel/component/go-sidepanel.component';
import { ConfirmDialogComponent, ConfirmDialogData } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageDialogComponent } from '@go-modules/components/dialogs/message-dialog/message-dialog.component';
import { LIBRARY_TYPES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import type { LibraryCollection } from '@go-modules/interfaces/library/library-collection';
import { ActiveBrandService } from '@go-modules/services/active-brand';
import {
	AccessibleGridReorderCancelEvent,
	AccessibleGridReorderDirective,
	AccessibleGridReorderFinalizeEvent,
	AccessibleGridReorderItemId,
	AccessibleGridReorderMoveEvent
} from '@go-modules/directives/accessible-grid-reorder/accessible-grid-reorder.directive';

export const MANAGE_COLLECTIONS_PANEL_TITLE_ID = 'manage-collections-panel-title';

const MANAGE_COLLECTIONS_GRID_COLUMNS = ['handle', 'edit', 'delete', 'visibility'] as const;

@Component({
	selector: 'ngx-manage-collections-panel',
	templateUrl: './manage-collections-panel.component.html',
	styleUrls: ['./manage-collections-panel.component.scss'],
	standalone: false
})
export class ManageCollectionsPanelComponent implements OnInit {
	public readonly panelTitleId = MANAGE_COLLECTIONS_PANEL_TITLE_ID;
	public readonly gridColumns = MANAGE_COLLECTIONS_GRID_COLUMNS;
	public readonly columnHeaderIds = {
		primary: 'manage-collections-column-primary',
		edit: 'manage-collections-column-edit',
		delete: 'manage-collections-column-delete',
		visibility: 'manage-collections-column-visible'
	} as const;
	public collections: LibraryCollection[];
	public copiedCollections: LibraryCollection[];
	public pendingSave = false;
	public saving$ = new BehaviorSubject(false);
	public libraryTypes = LIBRARY_TYPES;

	/**
	 * This panel now uses a composite grid reorder model instead of the legacy
	 * linear accessibleDropList pattern. Windows + NVDA needs the grid to own
	 * arrow-key navigation and focus restoration across multiple interactive
	 * cells in the row. Native controls are entered intentionally from the
	 * focused grid cell so checkbox/button semantics stay native while the grid
	 * keeps reliable arrow-key navigation.
	 */
	@ViewChild(AccessibleGridReorderDirective)
	public accessibleGridReorder: AccessibleGridReorderDirective | undefined;

	constructor (
		@Inject(GO_SIDEPANEL_DATA) public sidepanelData: {
			options: {
				inputs: {
					collections: LibraryCollection[]
				}
			};
		},
		public ngxGoSidepanelService: GoSidepanelService,
		private dialog: MatDialog,
		private dialogRef: GoDialogRef,
		private translate: TranslateService,
		private elementRef: ElementRef,
		private libraryService: LibraryService,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(fullstoryToken) private fullstoryService: FullstoryService,
		private activeBrandService: ActiveBrandService
	) {}

	public ngOnInit (): void {
		this.collections = this.sidepanelData.options.inputs.collections;
		this.copiedCollections = structuredClone(this.collections);
	}

	public get gridItemIds (): number[] {
		return this.copiedCollections.map((collection) => collection.id);
	}

	public readonly getCollectionLabel = (itemId: AccessibleGridReorderItemId): string => {
		const collection = this.copiedCollections.find(
			(currentCollection) => `${currentCollection.id}` === `${itemId}`
		);
		return collection?.name ?? '';
	};

	public getCollectionRowHeaderId (collectionId: number): string {
		return `collection-card-title-${collectionId}`;
	}

	public getCollectionCellHeaders (
		collectionId: number,
		columnHeaderId: string
	): string {
		return `${columnHeaderId} ${this.getCollectionRowHeaderId(collectionId)}`;
	}

	public getVisibilityCellAriaLabel (collection: LibraryCollection): string {
		const permission = this.findMyPermission(collection);
		const translationKey = permission.is_hidden
			? 'library-collections_manage-collections-visibility-unchecked'
			: 'library-collections_manage-collections-visibility-checked';

		return this.translate.instant(translationKey, {
			collection: collection.name
		});
	}

	public toggleCollectionVisibility (permission): void {
		permission.is_hidden = !permission.is_hidden;
		this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_TOGGLE_VISIBILITY, {});
		this.pendingSave = true;
	}

	public cancel (): void {
		this.dialogRef.close(this.collections);
	}

	public saveCollectionPermissions (): void {
		this.accessibleGridReorder?.finalizeActiveReorder(false, false);

		if (this.pendingSave) {
			const payload = {
				permissions: this.copiedCollections.map((collection) => this.findMyPermission(collection))
			};
			this.saving$.next(true);
			this.libraryService.updateCollectionPermission(payload).subscribe(() => this.saving$.next(false));
		}

		this.dialogRef.close(this.copiedCollections);
	}

	public canEditCollection (collection): boolean {
		return collection.type === 'shared' && collection.created_by === this.userService.currentUser.user_id;
	}

	public canDeleteCollection (collection): boolean {
		return collection.type === 'shared';
	}

	public deleteCollection (collection): void {
		const message = collection.created_by === this.userService.currentUser.user_id ?
			'library-collections_manage-collections-delete-owned' : 'library-collections_manage-collections-delete-shared';
		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(
			ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('library-collections_manage-collections-delete-title'),
					message: this.translate.instant(message, this.activeBrandService.getTranslationParams())
				} as ConfirmDialogData
			});

		dialogRef.afterClosed().pipe(
			filter((confirm: boolean) => confirm)
		).subscribe(() => {
			this.libraryService.removeCollection(collection)
				.subscribe({
					next: () => {
						this.copiedCollections.splice(this.copiedCollections.indexOf(collection), 1);
						this.collections.splice(this.collections.indexOf(collection), 1);
					},
					error: (err) => {
						let errMessage = 'library-collections_remove-collection-failed-message';
						let title = 'library-collections_remove-collection-failed-title';

						if (err.status === 409) {
							title = 'library-collections_remove-collection-with-items-title';
							errMessage = 'library-collections_remove-collection-with-items-message';
						}

						MessageDialogComponent.open(this.dialog, {
							data: {
								title: this.translate.instant(title),
								message: this.translate.instant(errMessage)
							}
						});
					}
				});
		});
	}

	public modifyCollection (collection = null): void {
		if (!collection && this.copiedCollections.filter((coll) => coll.type === 'shared' && coll.created_by === this.userService.currentUser.user_id).length >= clientSettings.SharedCollectionLimit) {
			MessageDialogComponent.open(this.dialog, {
				data: {
					title: this.translate.instant('library-collections_modify-collections-too-many-title'),
					message: this.translate.instant('library-collections_modify-collections-too-many-message')
				}
			});
			return;
		}

		if (collection == null) {
			this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_CREATE_NEW_COLLECTION, {});
		}

		const manageCollectionsPanel = this.elementRef.nativeElement;
		this.ngxGoSidepanelService.open(ModifyCollectionsPanelComponent, {
			inputs: {
				collection
			},
			noFooter: true,
			panelClass: ['modify-collections-panel']
		}, manageCollectionsPanel).afterClosed().subscribe((returnedCollection) => {
			if (!returnedCollection) {
				return;
			}

			const index = this.copiedCollections.findIndex(
				(currentCollection) => currentCollection.id === returnedCollection.id
			);
			if (index > -1) {
				this.copiedCollections[index] = returnedCollection;
				this.collections[index] = returnedCollection;
			} else {
				this.copiedCollections.push(returnedCollection);
				this.collections.push(returnedCollection);
			}
		});
	}

	public readonly findMyPermission = (collection) => {
		return collection.library_collection_permissions.find((permission) =>
			permission.user_id === this.userService.currentUser.user_id);
	};

	public drop (event: CdkDragDrop<LibraryCollection[]>): void {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		const movedCollection = this.copiedCollections[event.previousIndex];
		moveItemInArray(this.copiedCollections, event.previousIndex, event.currentIndex);
		this.pendingSave = true;
		this.accessibleGridReorder?.handleExternalDrop(movedCollection.id);
		this.fullstoryService.createEvent(FULLSTORY_EVENTS.NEW_LIBRARY_COLLECTIONS_REORDERED, {});
	}

	public onDragStarted (): void {
		this.accessibleGridReorder?.cancelActiveReorder(false, false);
	}

	public onDragEnded (): void {}

	public onGridReorderMove ({ currentIndex, newIndex }: AccessibleGridReorderMoveEvent): void {
		moveItemInArray(this.copiedCollections, currentIndex, newIndex);
	}

	public onGridReorderFinalize ({ moved }: AccessibleGridReorderFinalizeEvent): void {
		if (moved) {
			this.pendingSave = true;
		}
	}

	public onGridReorderCancel ({ currentIndex, originalIndex }: AccessibleGridReorderCancelEvent): void {
		if (currentIndex === originalIndex) {
			return;
		}

		moveItemInArray(this.copiedCollections, currentIndex, originalIndex);
	}
}
